import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { Zap, Sun, Flame, Droplets, Factory, Snowflake, Fuel, Building2, Trash2 } from 'lucide-react';
import { getLatestFactorYear } from '@/utils/emissionCalculator';

const ICON_MAP = {
    'Zap': Zap,
    'Sun': Sun,
    'Flame': Flame,
    'Droplets': Droplets,
    'Factory': Factory,
    'Snowflake': Snowflake,
    'Fuel': Fuel,
    'Building2': Building2,
    'Trash2': Trash2,
};

export function useVisibleCategories(currentOrg) {
    const [categories, setCategories] = useState([]);
    const [categoryGroups, setCategoryGroups] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function fetchCategories() {
            if (!currentOrg) return;

            setLoading(true);
            try {
                // 1. Fetch visible mappings
                const { data: mappings, error: mappingError } = await supabase
                    .from('emission_category_mappings')
                    .select('*')
                    .eq('is_visible', true);

                if (mappingError) throw mappingError;

                // 2. Fetch latest factors for the user's region to get Notes
                // To get the latest, we query the highest factor_year. 
                // We'll get all active factors to match against the mappings.
                const state = currentOrg.state?.toUpperCase() || 'NSW';
                const gridRegion = currentOrg.electricity_grid_region?.toUpperCase();

                // Use a generic query, then filter in memory for simplicity with regions
                const { data: allFactors, error: factorError } = await supabase
                    .from('emission_factors')
                    .select('category, factor_year, state, notes')
                    .eq('is_active', true)
                    .order('factor_year', { ascending: false });

                if (factorError) throw factorError;

                // 3. Process and map the data
                const processedCategories = mappings.map(mapping => {
                    // Find the most relevant factor note for this category
                    const categoryFactors = allFactors.filter(f => f.category === mapping.category_key);

                    let region = 'National';
                    let note = '';

                    if (categoryFactors.length > 0) {
                        // Find highest year
                        const highestYear = Math.max(...categoryFactors.map(f => f.factor_year));
                        const latestFactors = categoryFactors.filter(f => f.factor_year === highestYear);

                        // For electricity, try to match the exact grid region or state
                        if (mapping.category_key === 'electricity') {
                            const exactMatch = latestFactors.find(f => f.state === gridRegion) ||
                                latestFactors.find(f => f.state === state);

                            if (exactMatch) {
                                note = exactMatch.notes || '';
                                region = exactMatch.state || state;
                            }
                        } else {
                            // For others, just take the first note from the latest year
                            note = latestFactors[0]?.notes || '';
                        }
                    }

                    const IconComponent = ICON_MAP[mapping.icon] || Zap; // Fallback icon

                    return {
                        value: mapping.category_key,
                        label: mapping.label,
                        unit: mapping.unit,
                        icon: IconComponent,
                        color: mapping.color,
                        scope: mapping.scope,
                        rawNote: note,
                        rawRegion: region,
                        // Format: Category Name (with unit) : Region : Notes
                        formattedLabel: `${mapping.label} (${mapping.unit}) : ${region} : ${note}`.replace(/\s:\s*$/, '') // Clean trailing colons if empty note
                    };
                });

                // 4. Group by Scope for the dropdown
                const groups = {
                    'Scope 1 - Direct Emissions': processedCategories.filter(c => c.scope === 'Scope 1'),
                    'Scope 2 - Indirect Emissions': processedCategories.filter(c => c.scope === 'Scope 2'),
                    'Scope 3 - Value Chain Emissions': processedCategories.filter(c => c.scope === 'Scope 3')
                };

                // Remove empty groups
                Object.keys(groups).forEach(key => {
                    if (groups[key].length === 0) {
                        delete groups[key];
                    }
                });

                setCategories(processedCategories);
                setCategoryGroups(groups);
            } catch (err) {
                console.error("Error fetching visible categories:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }

        fetchCategories();
    }, [currentOrg]);

    return { flatCategories: categories, groupedCategories: categoryGroups, loading, error };
}
